import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Minus, Plus, Trash2, CreditCard, DollarSign, Truck } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import ZelleInstructions from "./ZelleInstructions";
import SmsConsent from "./SmsConsent";
import PromoField from "./PromoField";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, removeItem, total: subtotal, clear } = useCart();
  const [checkout, setCheckout] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", sms_consent: false });
  const [promo, setPromo] = useState(null);
  const { user: customer } = useCustomerAuth();
  useEffect(() => {
    if (customer) setForm((f) => ({ ...f, name: f.name || customer.name || "", email: f.email || customer.email || "" }));
  }, [customer]);
  const [method, setMethod] = useState("zelle");
  const [placing, setPlacing] = useState(false);
  const [zelleOrder, setZelleOrder] = useState(null);
  const [shippingCfg, setShippingCfg] = useState({ fee: 0, threshold: 0 });

  useEffect(() => {
    axios.get(`${API}/payment-info`).then(({ data }) => {
      setShippingCfg({ fee: data.merch_shipping_fee || 0, threshold: data.merch_free_shipping_threshold || 0 });
    }).catch(() => {});
  }, []);

  const discount = promo ? (promo.kind === "percent" ? Math.round(subtotal * promo.value) / 100 : promo.kind === "fixed" ? Math.min(promo.value, subtotal) : 0) : 0;
  const shipping = (promo?.free_shipping || subtotal >= shippingCfg.threshold) ? 0 : shippingCfg.fee;
  const total = Math.max(0, subtotal - discount) + shipping;
  const almostFree = shippingCfg.threshold - subtotal;

  const placeOrder = async (e) => {
    e.preventDefault();
    setPlacing(true);
    try {
      const payload = {
        ...form,
        items: items.map((i) => ({
          product_id: i.product_id, title: i.title, size: i.size, price: i.price, quantity: i.quantity, bundle_id: i.bundle_id || null,
        })),
        payment_method: method,
        origin_url: window.location.origin,
        promo_code: promo?.code || null,
      };
      const { data } = await axios.post(`${API}/orders`, payload);
      if (method === "card" && data.checkout_url) {
        clear();
        window.location.href = data.checkout_url;
        return;
      }
      // Zelle
      setZelleOrder(data.order);
      clear();
      setPromo(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Order failed. Try again.");
    } finally { setPlacing(false); }
  };

  const closeAll = () => {
    setZelleOrder(null);
    setCheckout(false);
    setOpen(false);
    setPromo(null);
    setForm({ name: "", email: "", phone: "", address: "", sms_consent: false });
  };

  return (
    <>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md">
          <SheetHeader>
            <SheetTitle className="font-display">Your Cart</SheetTitle>
          </SheetHeader>
          <div className="mt-6 flex flex-col h-[calc(100vh-8rem)]">
            {items.length === 0 ? (
              <div className="flex-1 flex items-center justify-center text-center">
                <div>
                  <p className="font-bold text-zinc-800">Cart is empty</p>
                  <p className="text-xs text-zinc-500 mt-1">Grab some crew merch from the section below.</p>
                </div>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-auto space-y-3">
                  {items.map((i) => (
                    <div key={i.key} data-testid={`cart-item-${i.product_id}`} className="flex gap-3 border-b border-zinc-200 pb-3">
                      <img src={i.image} alt={i.title} className="w-16 h-16 object-cover rounded-md" />
                      <div className="flex-1">
                        <div className="font-bold text-sm text-zinc-900">{i.title}</div>
                        <div className="text-xs text-zinc-500" data-testid={`cart-item-price-${i.product_id}`}>Size {i.size} · ${Number(i.price).toFixed(2)}
                          {i.bundle_id && (i.bundle_active
                            ? <span className="ml-1 text-emerald-700 font-bold">· {i.bundle_title} price</span>
                            : <span className="ml-1 text-amber-700 font-bold" data-testid={`cart-item-bundle-broken-${i.product_id}`}>· regular price (bundle incomplete)</span>)}
                        </div>
                        <div className="mt-1 flex items-center gap-1">
                          <button data-testid={`cart-qty-dec-${i.product_id}`} onClick={() => updateQty(i.key, -1)} className="w-6 h-6 border border-zinc-300 rounded"><Minus className="w-3 h-3 mx-auto" /></button>
                          <span className="font-mono-data text-sm w-6 text-center">{i.quantity}</span>
                          <button data-testid={`cart-qty-inc-${i.product_id}`} onClick={() => updateQty(i.key, 1)} className="w-6 h-6 border border-zinc-300 rounded"><Plus className="w-3 h-3 mx-auto" /></button>
                          <button data-testid={`cart-remove-${i.product_id}`} onClick={() => removeItem(i.key)} className="ml-auto w-6 h-6 text-zinc-500 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="border-t border-zinc-200 pt-4 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-600">Subtotal</span>
                    <span data-testid="cart-subtotal-amount" className="font-mono-data font-bold">${subtotal.toFixed(2)}</span>
                  </div>
                  <PromoField subtotal={subtotal} promo={promo} email={customer?.email || form.email} onApply={(p) => { setPromo(p); toast.success(p.message); }} onClear={() => setPromo(null)} />
                  {discount > 0 && (
                    <div className="flex justify-between text-sm text-emerald-700">
                      <span>Discount ({promo.code})</span>
                      <span data-testid="cart-discount-amount" className="font-mono-data font-bold">−${discount.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-600 flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> Shipping</span>
                    <span data-testid="cart-shipping-amount" className="font-mono-data font-bold">
                      {shipping === 0 ? <span className="text-emerald-600">FREE</span> : `$${shipping.toFixed(2)}`}
                    </span>
                  </div>
                  {shipping > 0 && almostFree > 0 && (
                    <div className="text-[11px] text-[#0C2E2B] bg-[#FFDF00]/25 border-l-2 border-[#FFDF00] px-2 py-1.5 rounded">
                      Add <b>${almostFree.toFixed(2)}</b> more to unlock free shipping
                    </div>
                  )}
                  <div className="flex justify-between font-bold pt-1 border-t border-zinc-100">
                    <span>Total</span>
                    <span data-testid="cart-total-amount" className="font-mono-data text-lg">${total.toFixed(2)}</span>
                  </div>
                  <Button
                    data-testid="cart-drawer-checkout-btn"
                    className="btn-kinetic rounded-md w-full py-6 text-sm"
                    onClick={() => setCheckout(true)}
                  >
                    Checkout
                  </Button>
                </div>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <Dialog open={checkout} onOpenChange={(o) => { if (!o) closeAll(); else setCheckout(o); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">{zelleOrder ? "Almost done" : "Checkout"}</DialogTitle>
          </DialogHeader>

          {zelleOrder ? (
            <ZelleInstructions
              zelle={zelleOrder.zelle}
              refId={zelleOrder.ref_id}
              purpose="order"
              onDone={closeAll}
            />
          ) : (
            <form onSubmit={placeOrder} className="space-y-3" data-testid="checkout-form">
              <Input data-testid="checkout-name-input" required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <Input data-testid="checkout-email-input" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              <Input data-testid="checkout-phone-input" required placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              <Input data-testid="checkout-address-input" required placeholder="Shipping address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
              <SmsConsent checked={form.sms_consent} onChange={(v) => setForm({ ...form, sms_consent: v })} context="order" testId="checkout-sms-consent" />

              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-zinc-700 mb-2">Payment</div>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    data-testid="checkout-method-zelle-btn"
                    onClick={() => setMethod("zelle")}
                    className={`p-3 rounded-md border-2 text-left transition-colors ${method === "zelle" ? "border-[#FFDF00] bg-[#FFDF00]/20" : "border-zinc-200 bg-white"}`}
                  >
                    <DollarSign className="w-4 h-4 text-[#0C2E2B] mb-1" />
                    <div className="font-bold text-sm text-[#0C2E2B]">Zelle</div>
                    <div className="text-[10px] text-zinc-500">No fees · direct to us</div>
                  </button>
                  <button
                    type="button"
                    data-testid="checkout-method-card-btn"
                    onClick={() => setMethod("card")}
                    className={`p-3 rounded-md border-2 text-left transition-colors ${method === "card" ? "border-[#FFDF00] bg-[#FFDF00]/20" : "border-zinc-200 bg-white"}`}
                  >
                    <CreditCard className="w-4 h-4 text-[#0C2E2B] mb-1" />
                    <div className="font-bold text-sm text-[#0C2E2B]">Card</div>
                    <div className="text-[10px] text-zinc-500">Stripe secure</div>
                  </button>
                </div>
              </div>

              <div className="pt-2 space-y-1 border-t border-zinc-100">
                <div className="flex justify-between text-xs text-zinc-600">
                  <span>Subtotal</span>
                  <span className="font-mono-data">${subtotal.toFixed(2)}</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-xs text-emerald-700">
                    <span>Discount ({promo.code})</span>
                    <span data-testid="checkout-discount-amount" className="font-mono-data">−${discount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-xs text-zinc-600">
                  <span>Shipping</span>
                  <span className="font-mono-data">{shipping === 0 ? "FREE" : `$${shipping.toFixed(2)}`}</span>
                </div>
                <div className="flex justify-between text-sm font-bold pt-1">
                  <span>Total</span>
                  <span data-testid="checkout-total-amount" className="font-mono-data">${total.toFixed(2)}</span>
                </div>
              </div>
              <Button data-testid="checkout-submit-btn" disabled={placing} className="btn-kinetic rounded-md w-full py-5">
                {placing ? "Placing order..." : method === "card" ? "Continue to card" : "Place order (Pay by Zelle)"}
              </Button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
